import { resolveColorId } from '../../colors';

const getVariableSchema = ({
  sortedSiteVars,
  units,
  variableSources,
  DEFAULT_AGGREGATION_OPTIONS,
  signalCounts,
}) => ({
  maxItems: 24,
  minItems: signalCounts?.minSignal || 1,
  type: 'array',
  title: 'Signals',
  items: {
    type: 'object',
    required: ['id', 'aggregate'],
    properties: {
      id: {
        type: 'string',
        title: 'Signal',
        default: sortedSiteVars?.[0]?.id,
        placeholder: 'Select signal',
        noOptionsMessage: 'No signals available',
        // use anyOf here even though not multi select because of issues with oneOf when many options
        anyOf: sortedSiteVars?.length
          ? sortedSiteVars.map((v) => ({
              enum: [v.id],
              title: `${v.stateset ? v.stateset.name : ''} [ ${
                variableSources.find((s) => s.id === v.source_id).name || '-'
              } ] [ ${v.itemDesignation || '-'} ] ${v.name}`,
            }))
          : [],
      },
      label: {
        type: 'string',
        title: 'Label',
      },
      unit: {
        type: 'string',
        title: 'Unit',
        default: units[0].id,
        oneOf: units.map((unit) => ({
          enum: [unit.id],
          title: `${unit.category}: ${unit.name}`,
        })),
      },
      unitLabel: {
        type: 'string',
        title: 'Custom Unit Label',
        description: 'Custom label to display instead of the unit name',
      },
      decimals: {
        type: 'string',
        title: 'Decimals',
        default: 'auto',
        enum: ['auto', '0', '1', '2', '3', '4', '5'],
      },
    },
    dependencies: {
      // https://github.com/rjsf-team/react-jsonschema-form/issues/1703
      id: {
        oneOf: variableSources.map((source) => {
          const { variables = [null], aggregationOptions, aggregationOptionNames } = source;

          const fillOptions = [
            { enum: ['null'], title: 'Show gaps (no interpolation)' },
            {
              enum: ['connect'],
              title: 'Connect data points (linear interpolation)',
            },
            {
              enum: ['connect-monotone'],
              title: 'Connect data points (polynomial interpolation)',
            },
          ];
          if (source.type === 'dec') {
            fillOptions.push(
              {
                enum: ['previous'],
                title: 'Fill missing data points using previous value (linear interpolation)',
              },
              {
                enum: ['previous-monotone'],
                title: 'Fill missing data points using previous value (polynomial interpolation)',
              }
            );
          }

          let aggOptions = DEFAULT_AGGREGATION_OPTIONS.map(({ value, label }) => ({
            enum: [value],
            title: label,
          }));

          if (aggregationOptions && aggregationOptionNames) {
            aggOptions = aggregationOptions.map((agg, index) => ({
              enum: [agg],
              title: aggregationOptionNames[index],
            }));
          } else if (source.options?.aggregationOptions) {
            aggOptions = aggregationOptions.map((agg) => ({
              enum: [agg],
              title: agg.charAt(0).toUpperCase() + agg.slice(1),
            }));
          }

          const sourceOptions = {
            properties: {
              id: { enum: variables },
              aggregate: {
                type: 'string',
                title: 'Aggregate',
                default: 'max',
                oneOf: aggOptions,
              },
              fill: {
                title: 'Fill',
                description: 'How to fill any gaps in the series data',
                type: 'string',
                default: 'null',
                oneOf: fillOptions,
              },
            },
          };

          if (source.type === 'dummy') {
            sourceOptions.properties.quality = {
              title: 'Quality',
              description: 'Set data quality',
              type: 'string',
              default: '1',
              oneOf: [
                { enum: ['1'], title: 'Valid' },
                { enum: ['2'], title: 'Invalid' },
                { enum: ['random'], title: 'Random' },
              ],
            };
          }

          return sourceOptions;
        }),
      },
    },
  },
});

const updateVisualizationSchema = ({ visualization, variables, schema, colors }) => {
  switch (visualization.type) {
    case 'duval':
    case 'duvalPentagon': {
      delete schema.properties.plots;
      break;
    }
    case 'star': {
      delete schema.properties.plot;
      break;
    }
    default: {
      break;
    }
  }

  if (visualization.type === 'graph' && variables.length > 0) {
    schema.properties.series = {
      type: 'array',
      title: 'Series',
      items: {
        type: 'object',
        properties: {
          axis: {
            type: 'string',
            title: 'Y-axis position',
            default: 'left',
            enum: ['left', 'right'],
          },
          id: {
            type: 'string',
            title: 'Signal id',
            default: `${variables[0].id}.${variables[0].aggregate}`,
            oneOf: variables.map((variable) => ({
              enum: [`${variable.id}.${variable.aggregate}`],
              title: variable.seriesLabel,
            })),
          },
          color: {
            type: 'string',
            title: 'Color',
            default: colors[0].id,
            oneOf: colors.map((color) => ({
              enum: [color.id],
              title: color.label,
            })),
          },
        },
      },
    };
  }

  if (visualization.type === 'duval' && variables.length > 0) {
    const duvalSignalIdSchema = {
      type: 'string',
      default: `${variables[0].id}.${variables[0].aggregate}`,
      oneOf: variables.map((variable) => ({
        enum: [`${variable.id}.${variable.aggregate}`],
        title: variable.seriesLabel,
      })),
    };
    schema.properties.plots = {
      maxItems: 3,
      type: 'array',
      title: 'Plots',
      items: {
        type: 'object',
        title: 'Configure plot',
        properties: {
          label: {
            type: 'string',
            title: 'Label',
          },
          color: {
            type: 'string',
            title: 'Color',
            default: colors[0].id,
            oneOf: colors.map((color) => ({
              enum: [color.id],
              title: color.label,
            })),
          },
          rollingAverageColor: {
            type: 'string',
            title: 'Rolling average color',
            default: colors[1].id,
            oneOf: colors.map((color) => ({
              enum: [color.id],
              title: color.label,
            })),
          },
          leftId: {
            title: 'Left axis signal',
            ...duvalSignalIdSchema,
          },
          rightId: {
            title: 'Right axis signal',
            ...duvalSignalIdSchema,
          },
          bottomId: {
            title: 'Bottom axis signal',
            ...duvalSignalIdSchema,
          },
        },
      },
    };
  }

  if (visualization.type === 'duvalPentagon' && variables.length > 0) {
    const duvalSignalIdSchema = {
      type: 'string',
      default: `${variables[0].id}.${variables[0].aggregate}`,
      oneOf: variables.map((variable) => ({
        enum: [`${variable.id}.${variable.aggregate}`],
        title: variable.seriesLabel,
      })),
    };
    schema.properties.plots = {
      maxItems: 3,
      type: 'array',
      title: 'Plots',
      items: {
        type: 'object',
        title: 'Configure plot',
        properties: {
          label: {
            type: 'string',
            title: 'Label',
          },
          color: {
            type: 'string',
            title: 'Color',
            default: colors[0].id,
            oneOf: colors.map((color) => ({
              enum: [color.id],
              title: color.label,
            })),
          },
          rollingAverageColor: {
            type: 'string',
            title: 'Rolling average color',
            default: colors[1].id,
            oneOf: colors.map((color) => ({
              enum: [color.id],
              title: color.label,
            })),
          },
          h2Id: {
            title: 'H2 axis signal',
            ...duvalSignalIdSchema,
          },
          c2h6Id: {
            title: 'C2H6 axis signal',
            ...duvalSignalIdSchema,
          },
          ch4Id: {
            title: 'CH4 axis signal',
            ...duvalSignalIdSchema,
          },
          c2h4Id: {
            title: 'C2H4 axis signal',
            ...duvalSignalIdSchema,
          },
          c2h2Id: {
            title: 'C2H2 axis signal',
            ...duvalSignalIdSchema,
          },
        },
      },
    };
  }

  if (visualization.type === 'star' && variables.length > 0) {
    // Each axis defaults to its own signal slot (L1/X -> variables[0], L2/Y -> variables[1], L3 -> variables[2])
    const starSignalIdSchema = (index) => {
      const defaultVariable = variables[index] || variables[0];
      return {
        type: 'string',
        default: `${defaultVariable.id}.${defaultVariable.aggregate}`,
        oneOf: variables.map((variable) => ({
          enum: [`${variable.id}.${variable.aggregate}`],
          title: variable.seriesLabel,
        })),
      };
    };
    schema.properties.inputMode = {
      type: 'string',
      title: 'Input mode',
      description:
        'Cartesian plotting assumes that included X and Y signals are pre-processed and will disable rolling average functionality.\n\nVector-based plotting can be used on raw signals for comparisons and will enable rolling average functionality.',
      showTooltip: true,
      default: 'vectors',
      oneOf: [
        { enum: ['vectors'], title: 'Vectors (L1, L2, L3)' },
        { enum: ['cartesian'], title: 'Cartesian (X, Y)' },
      ],
    };

    schema.dependencies = {
      inputMode: {
        oneOf: [
          {
            properties: {
              inputMode: { enum: ['vectors'] },
              plot: {
                type: 'object',
                title: 'Assign signals to plot vectors',
                properties: {
                  label: {
                    type: 'string',
                    title: 'Label',
                  },
                  L1: {
                    title: 'Vector 1 signal id',
                    ...starSignalIdSchema(0),
                  },
                  L2: {
                    title: 'Vector 2 signal id',
                    ...starSignalIdSchema(1),
                  },
                  L3: {
                    title: 'Vector 3 signal id',
                    ...starSignalIdSchema(2),
                  },
                  color: {
                    type: 'string',
                    title: 'Color',
                    default: colors[0].id,
                    oneOf: colors.map((color) => ({
                      enum: [color.id],
                      title: color.label,
                    })),
                  },
                },
              },
            },
          },
          {
            properties: {
              inputMode: { enum: ['cartesian'] },
              plot: {
                type: 'object',
                title: 'Assign signals to plot axes',
                properties: {
                  label: {
                    type: 'string',
                    title: 'Label',
                  },
                  X: {
                    title: 'X axis signal id',
                    ...starSignalIdSchema(0),
                  },
                  Y: {
                    title: 'Y axis signal id',
                    ...starSignalIdSchema(1),
                  },
                  color: {
                    type: 'string',
                    title: 'Color',
                    default: colors[0].id,
                    oneOf: colors.map((color) => ({
                      enum: [color.id],
                      title: color.label,
                    })),
                  },
                },
              },
            },
          },
        ],
      },
    };
  }

  if (visualization.type === 'grouped-bar') {
    const groupedBarSignals = (visualization.variables || [])
      .map((visualizationVariable) => {
        const siteVariable = variables.find((variable) => variable.id === visualizationVariable.id);
        return siteVariable ? { ...siteVariable, ...visualizationVariable } : null;
      })
      .filter(Boolean);

    if (groupedBarSignals.length > 0) {
      schema.properties.groups = {
        type: 'array',
        title: 'Groups',
        maxItems: 8,
        items: {
          type: 'object',
          required: ['signals', 'groupLabel'],
          properties: {
            signals: {
              type: 'array',
              title: 'Signals',
              minItems: 1,
              maxItems: 3,
              items: {
                required: ['id', 'signalLabel'],
                properties: {
                  id: {
                    type: 'string',
                    title: 'Signal',
                    default: groupedBarSignals[0].id,
                    placeholder: 'Select signal',
                    noOptionsMessage: 'No signals available',
                    // use anyOf here even though not multi select because of issues with oneOf when many options
                    anyOf: groupedBarSignals.map((signal) => ({
                      enum: [signal.id],
                      title: `[ ${signal.itemDesignation || '-'} ] ${signal.name}`,
                    })),
                  },
                  signalLabel: {
                    type: 'string',
                    title: 'Signal label',
                    description:
                      'This label will be used to commonly identify signal values across groups.',
                  },
                },
              },
            },
            groupLabel: {
              type: 'string',
              title: 'Group label',
              description:
                'This label will be used to identify the signal groups in the visualization.',
            },
          },
        },
      };
    }
  }

  return schema;
};

// Priority signals to attempt to keep when saving a visualizations and
// removing signals that overflow the maxSignal amount for a given visualization type.
const getPrioritySignalIds = (visualization) => {
  const configuration = visualization.configuration || {};
  let signalReferences;

  switch (visualization.type) {
    case 'star': {
      const plotKeys = configuration.inputMode === 'cartesian' ? ['X', 'Y'] : ['L1', 'L2', 'L3'];
      const plot = configuration.plot || {};
      signalReferences = plotKeys.map((key) => plot[key]);
      break;
    }
    case 'duval': {
      signalReferences = (configuration.plots || []).flatMap((plot) => [
        plot.leftId,
        plot.rightId,
        plot.bottomId,
      ]);
      break;
    }
    case 'duvalPentagon': {
      signalReferences = (configuration.plots || []).flatMap((plot) => [
        plot.h2Id,
        plot.c2h6Id,
        plot.ch4Id,
        plot.c2h4Id,
        plot.c2h2Id,
      ]);
      break;
    }
    case 'graph': {
      signalReferences = (configuration.series || []).map((seriesItem) => seriesItem.id);
      break;
    }
    case 'bar-gauge': {
      signalReferences = configuration.singleSignal ? [configuration.singleSignalId] : [];
      break;
    }
    default: {
      return [];
    }
  }

  // Dedupe so a signal reused across multiple axes/plots only consumes one maxSignal slot.
  return [...new Set(signalReferences.filter(Boolean).map((ref) => String(ref).split('.')[0]))];
};

const normalizeThresholds = (thresholds = {}) => ({
  ...thresholds,
  linearItems: thresholds.linearItems?.map((item) => ({
    ...item,
    fill: resolveColorId(item.fill),
  })),
  areaItems: thresholds.areaItems?.map((item) => ({
    ...item,
    fill: resolveColorId(item.fill),
  })),
});

const normalizeVisualizationConfigurationColors = (type, configuration = {}) => {
  switch (type) {
    case 'graph': {
      return {
        ...configuration,
        thresholds: normalizeThresholds(configuration.thresholds),
        series: configuration.series?.map((item) => ({
          ...item,
          color: resolveColorId(item.color),
        })),
      };
    }
    case 'star': {
      return {
        ...configuration,
        thresholds: normalizeThresholds(configuration.thresholds),
        plot: configuration.plot
          ? { ...configuration.plot, color: resolveColorId(configuration.plot.color) }
          : configuration.plot,
        rollingAverage: {
          ...configuration.rollingAverage,
          ...(configuration.rollingAverage?.show
            ? { color: resolveColorId(configuration.rollingAverage.color) }
            : {}),
        },
      };
    }
    case 'duval':
    case 'duvalPentagon': {
      return {
        ...configuration,
        plots: configuration.plots?.map((plot) => ({
          ...plot,
          color: resolveColorId(plot.color),
          rollingAverageColor: resolveColorId(plot.rollingAverageColor),
        })),
      };
    }
    case 'gauge':
    case 'bar-gauge':
    case 'grouped-bar':
    case 'status': {
      return {
        ...configuration,
        thresholds: normalizeThresholds(configuration.thresholds),
      };
    }
    default: {
      return configuration;
    }
  }
};

export {
  getVariableSchema,
  updateVisualizationSchema,
  getPrioritySignalIds,
  normalizeVisualizationConfigurationColors,
};
